import { useEffect } from 'react'

export interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info' | 'confirm'
  title: string
  message?: string
  duration?: number
  onConfirm?: () => void
  onCancel?: () => void
}

interface NotificationSystemProps {
  notifications: Notification[]
  onRemove: (id: string) => void
}

export default function NotificationSystem({ notifications, onRemove }: NotificationSystemProps) {
  useEffect(() => {
    notifications.forEach(notification => {
      if (notification.duration !== 0) {
        const timer = setTimeout(() => {
          onRemove(notification.id)
        }, notification.duration || 5000)

        return () => clearTimeout(timer)
      }
    })
  }, [notifications, onRemove])

  const getNotificationStyles = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800'
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800'
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800'
      case 'info':
        return 'bg-blue-50 border-blue-200 text-blue-800'
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800'
    }
  }

  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return '✅'
      case 'error':
        return '❌'
      case 'warning':
        return '⚠️'
      case 'info':
        return 'ℹ️'
      default:
        return 'ℹ️'
    }
  }

  if (notifications.length === 0) return null

  return (
    <div className={`fixed top-24 right-4 space-y-2 max-w-sm ${notifications.some(n => n.type === 'confirm') ? 'z-[100]' : 'z-[60]'}`}>
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className={`
            ${getNotificationStyles(notification.type)}
            border rounded-lg p-4 shadow-lg
            transform transition-all duration-300 ease-in-out
            animate-slide-in-left
            ${notification.type === 'confirm' ? 'z-[100]' : 'z-50'}
          `}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <span className="text-lg flex-shrink-0">
                {getIcon(notification.type)}
              </span>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium">
                  {notification.title}
                </h4>
                {notification.message && (
                  <p className="mt-1 text-sm opacity-90 whitespace-pre-line">
                    {typeof notification.message === 'string' ? notification.message : JSON.stringify(notification.message)}
                  </p>
                )}
                
                {/* Przyciski dla powiadomień z potwierdzeniem */}
                {notification.type === 'confirm' && (
                  <div className="mt-3 flex space-x-2">
                    <button
                      onClick={() => {
                        notification.onConfirm?.()
                        onRemove(notification.id)
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-medium"
                    >
                      OK
                    </button>
                    <button
                      onClick={() => {
                        notification.onCancel?.()
                        onRemove(notification.id)
                      }}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-xs font-medium"
                    >
                      Anuluj
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Przycisk X - ukryty dla powiadomień z potwierdzeniem */}
            {notification.type !== 'confirm' && (
              <button
                onClick={() => onRemove(notification.id)}
                className="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors duration-200"
                aria-label="Zamknij powiadomienie"
              >
                <span className="text-lg">×</span>
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
